import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  InputBase,
  IconButton,
  makeStyles,
  Typography
} from "@material-ui/core";
import Clear from "@material-ui/icons/Clear";
import SearchIcon from "@material-ui/icons/Search";
import OptimizedTooltip from "./OptimizedTooltip";
import { remCalc } from "../../utils/helpers";
import clsx from "clsx";

const useStyles = makeStyles(theme => ({
  inputWrapper: {
    width: remCalc(216),
    height: theme.spacing(4.5),
    borderRadius: theme.spacing(0.5),
    backgroundColor: "#F7F7F7",
    boxSizing: "border-box",
    border: "1px solid #ddd",
    display: "flex",
    alignItems: "center",
    "& .MuiInputBase-adornedStart": {
      "& > input": {
        marginLeft: theme.spacing(1)
      }
    },
    "&:hover": {
      backgroundColor: "#eee"
    },
    "&:focus-within": {
      backgroundColor: "#fff",
      border: `2px solid ${theme.palette.primary.main}`
    }
  },
  shortcut: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: "1px solid #EAEBF0",
    backgroundColor: "#fff",
    fontWeight: "bold",
    color: "#606165",
    lineHeight: "1rem",
    borderRadius: theme.spacing(0.25),
    marginRight: theme.spacing(1.5),
    padding: theme.spacing(0.25),
    height: 16,
    width: ({ isFocused }) => (isFocused ? 22 : 16)
  },
  clearTooltip: {
    fontFamily: "Roboto",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "14px",
    color: "#fff",
    "& > span": {
      padding: "0 2px",
      borderRadius: "2px",
      backgroundColor: "#7D7E82",
      color: "#fff"
    }
  }
}));

function SmallSearchBox({
  className = "",
  placeholder = "Search",
  showSearchField,
  isEnhancedSearch,
  focusedOuter,
  setFocusedOuter,
  disableForwardSlashFocus,
  inputId,
  ...props
}) {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef();

  const classes = useStyles({ isFocused });

  const handleForwardSlashFocus = useCallback(event => {
    if (
      event.keyCode === 191 &&
      !["input", "textarea"].includes(event.target.tagName.toLowerCase())
    ) {
      inputRef.current.focus();
    }
  }, []);

  const handleEscape = useCallback(event => {
    if (event.keyCode === 27) {
      if (props.onSearchChanged) {
        props.onSearchChanged("");
      }
      inputRef.current.blur();
    }
  }, []);

  useEffect(() => {
    if (!disableForwardSlashFocus) {
      document.addEventListener("keyup", handleForwardSlashFocus, false);
      document.addEventListener("keyup", handleEscape, false);

      return () => {
        document.removeEventListener("keyup", handleForwardSlashFocus, false);
        document.removeEventListener("keyup", handleEscape, false);
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableForwardSlashFocus]);

  // useEffect(() => {
  //   if (inputRef.current && showSearchField) {
  //     setTimeout(() => inputRef.current.focus(), 500);
  //   }
  // }, [showSearchField]);

  return (
    <Box className={clsx(classes.inputWrapper, className)}>
      <Box flexGrow="1" display="flex" pl={1.5} alignItems="center">
        <InputBase
          inputRef={inputRef}
          placeholder={placeholder}
          inputProps={{
            "aria-label": props.ariaLabel,
            "data-cy": props.dataCy,
            id: inputId || "project-searchInput",
            autoComplete: "off"
          }}
          value={props.searchText}
          onFocus={() => {
            setIsFocused(true);
            setFocusedOuter && setFocusedOuter(true);
          }}
          onBlur={() => {
            setIsFocused(false);
            setFocusedOuter && setFocusedOuter(false);
          }}
          onChange={e => props.onSearchChanged(e.target.value)}
          style={{ width: "100%" }}
          startAdornment={
            isEnhancedSearch ? (
              <SearchIcon color={isFocused ? "primary" : "root"} />
            ) : null
          }
        />
      </Box>
      {!!props.searchText.length ? (
        <OptimizedTooltip
          placement="bottom"
          title={
            <Typography className={classes.clearTooltip}>
              Close search <span>Esc</span>
            </Typography>
          }
        >
          <Box display="flex" alignItems="center" pr={0.75} mr={-0.375}>
            <IconButton
              size="small"
              onMouseDown={e => {
                e.stopPropagation();
                inputRef.current.blur();
                props.onSearchChanged("");
              }}
            >
              <Clear />
            </IconButton>
          </Box>
        </OptimizedTooltip>
      ) : !isEnhancedSearch ? (
        <OptimizedTooltip placement="bottom" title="Search">
          <Box display="flex" alignItems="center" pr={0.75}>
            <SearchIcon color="root" />
          </Box>
        </OptimizedTooltip>
      ) : (
        !isFocused && <Box className={classes.shortcut}>/</Box>
      )}
    </Box>
  );
}

export default SmallSearchBox;
